import { useRef, useState } from 'react';
import { INDIVIDUAL_UI_TYPE } from '../constants';
import { WellbeingMeasureQuestionnaire_wellbeingMeasureQuestionnaire_questions as Question } from '../__generated__/WellbeingMeasureQuestionnaire';

export type Answer =
  | { kind: 'numeric'; questionId: string; value: number }
  | { kind: 'daysAndHours'; questionId: string; days: number; hours: number };

/**
 * Step, answers and the sitting's start time.
 *
 * `startedAt` is captured on mount. The modal is mounted only while open, so
 * closing it discards the answers and re-opening produces a fresh sitting with
 * a fresh timestamp — the reset the acceptance criteria describe falls out of
 * the lifecycle rather than being a step to remember.
 */
export const useWellbeingMeasureSurvey = (questions: Question[]) => {
  const startedAt = useRef(new Date().toISOString()).current;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const currentQuestion = questions[step];
  const isFinalQuestion = step === questions.length - 1;
  const isComplete = step >= questions.length;

  /**
   * Derived, not indexed: an "Individual" question that is not the last one
   * advances on selection. That gives the designed behaviour — the five WHO-5
   * items auto-advance, absenteeism is "Days And Hours" so it gets a Next
   * button, and presenteeism is last so it gets Submit — without hardcoding
   * question positions, so a reordered set keeps working.
   */
  const autoAdvances =
    currentQuestion?.uiType === INDIVIDUAL_UI_TYPE && !isFinalQuestion;

  const currentAnswer = answers.find(
    entry => entry.questionId === currentQuestion?.id,
  );

  const answer = (value: Answer) =>
    setAnswers(previous => [
      ...previous.filter(entry => entry.questionId !== value.questionId),
      value,
    ]);

  const goBack = () => setStep(previous => Math.max(0, previous - 1));
  const goForward = () =>
    setStep(previous => Math.min(questions.length, previous + 1));

  return {
    step,
    currentQuestion,
    currentAnswer,
    startedAt,
    isComplete,
    autoAdvances,
    isFinalQuestion,
    answers,
    answer,
    goBack,
    goForward,
  };
};
